import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays the active product option value.
 * It is intended for the selected size label/value pair on product pages.
 *
 * @extends {Component}
 */
class SizeDescription extends Component {
  /** @type {Element | null} */
  #target = null;

  connectedCallback() {
    super.connectedCallback();
    this.#target = this.closest('.shopify-section, dialog');
    if (!this.#target) return;
    this.#target.addEventListener(ThemeEvents.variantUpdate, this.updateDescription);
    this.syncInitialState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#target?.removeEventListener(ThemeEvents.variantUpdate, this.updateDescription);
    this.#target = null;
  }

  /**
   * Updates the rendered description when the active variant changes.
   * @param {VariantUpdateEvent} event
   */
  updateDescription = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newSizeDescription = event.detail.data.html.querySelector(
      `size-description[data-block-id="${this.dataset.blockId}"]`
    );

    if (!newSizeDescription) {
      this.dataset.empty = 'true';
      return;
    }

    this.dataset.productId = newSizeDescription.dataset.productId ?? this.dataset.productId;
    this.dataset.hasValue = newSizeDescription.dataset.hasValue ?? 'false';
    this.dataset.empty =
      this.dataset.hasValue === 'true' && this.hasCompleteSelection(event.target) ? 'false' : 'true';

    morph(this, newSizeDescription, { childrenOnly: true });
  };

  syncInitialState() {
    const hasVariantParam = new URL(window.location.href).searchParams.has('variant');
    const hasValue = this.dataset.hasValue === 'true';

    this.dataset.empty = hasValue && (hasVariantParam || this.hasCompleteSelection()) ? 'false' : 'true';
  }

  /**
   * Checks whether all option groups in the related picker currently have a selection.
   * @param {EventTarget | null} [source]
   * @returns {boolean}
   */
  hasCompleteSelection(source = null) {
    const variantPicker =
      source instanceof HTMLElement && source.matches('variant-picker')
        ? source
        : this.#target?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);

    if (!variantPicker) return false;

    const fieldsets = [...variantPicker.querySelectorAll('fieldset[data-fieldset-index]')];
    const selects = [...variantPicker.querySelectorAll('select.variant-option__select')];

    const hasCompleteFieldsets = fieldsets.every((fieldset) => fieldset.querySelector('input:checked'));
    const hasCompleteSelects = selects.every((select) => select.selectedOptions.length > 0);

    return hasCompleteFieldsets && hasCompleteSelects;
  }
}

if (!customElements.get('size-description')) {
  customElements.define('size-description', SizeDescription);
}
